import { Spinner } from '@change/ds-core-ui';

import { getFacebookLocale } from '@change-corgi/config/facebook';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import { PetitionCommentPageContextProvider, prefetchPageContext } from 'src/app/pages/petitionComment/pageContext';
import type { PetitionCommentPagePrefetchedDataProps } from 'src/app/pages/petitionComment/pageContext';
import { addSchemeToUrl } from 'src/app/shared/utils/assets';
import { isLoaded } from 'src/app/shared/utils/async';
import { stripEmojis, truncate } from 'src/app/shared/utils/string';

import { Layout } from '../layout';

import { PetitionCommentErrorHandler } from './ErrorHandler';

const DEFAULT_IMAGE = '/images/default-petition.gif';
const DEFAULT_IMAGE_WIDTH = 1120;
const DEFAULT_IMAGE_HEIGHT = 630;

export const PetitionComment = withPrefetchedData(
	function PetitionComment({ prefetchedData }: PetitionCommentPagePrefetchedDataProps) {
		const { slug, id } = useKnownAvailableParams<{ slug: string; id: string }>();

		return (
			<PetitionCommentPageContextProvider
				commentId={id}
				petitionSlug={slug}
				prefetchedData={prefetchedData}
				errorHandler={PetitionCommentErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</PetitionCommentPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,

		// oxlint-disable-next-line typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context.params.id!, context),
		pageTitle: ({ prefetchedData, i18n, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { user } = prefetchedData.pageData.pageData.comment;
			return fromTemplate(i18n.translate('fe.pages.comment.page_title', { name: user.displayName }));
		},
		pageMetadata: ({ prefetchedData, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return {};
			}
			const { comment, petition } = prefetchedData.pageData.pageData;
			const title = truncate(stripEmojis(`${comment.user.firstName}: ${comment.comment}`), 400);
			const fbLocale = getFacebookLocale(petition.originalLocale.localeCode) || getFacebookLocale('en-US');

			const petitionPhoto = petition.photo?.petitionLarge;
			const imageUrl = addSchemeToUrl(petition.photo?.petitionLarge.url);

			return {
				twitterCard: 'summary_large_image' as const,
				ogLocale: fbLocale,
				ogTitle: title,
				ogDescription: petition.ask,
				twitterImageAlt: title,
				...(petitionPhoto && imageUrl
					? {
							ogImage: imageUrl,
							ogImageSecureUrl: imageUrl,
							ogImageWidth: petitionPhoto.width,
							ogImageHeight: petitionPhoto.height,
							twitterImage: imageUrl,
						}
					: {
							ogImage: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageSecureUrl: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageWidth: DEFAULT_IMAGE_WIDTH,
							ogImageHeight: DEFAULT_IMAGE_HEIGHT,
							twitterImage: getStaticAssetUrl(DEFAULT_IMAGE),
						}),
			};
		},
	},
);
